import React, { forwardRef, HTMLAttributes, useContext } from 'react';

import clsx from 'clsx';
import styled from '@emotion/styled';
import { variant, ComponentsType } from '@lucid/theme';

import { NavbarContext } from './navbar';

export type NavbarInnerContainerVariants = 'default';

export interface NavbarInnerContainerProps
  extends HTMLAttributes<HTMLOListElement> {
  /** Sets the background for the NavbarInnerContainer component. */
  background?: string;
  /** The available variants for the NavbarInnerContainer component. */
  variant?: ComponentsType['Navbar']['InnerContainer'];
}

export interface NavbarInnerContainerListProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the InnerContainerList is expanded. */
  isExpanded?: boolean;
  /** The available variants for the NavbarInnerContainerList component. */
  variant?: ComponentsType['Navbar']['InnerContainer'];
}

const NavbarInnerContainerList = styled.ol<NavbarInnerContainerListProps>(
  variant({
    scale: 'navbar.innerContainer',
    variants: {
      defaultVariant: {}
    }
  }),
  ({ theme }) => ({
    label: 'navbarInnerContainer',
    display: 'flex',
    order: 2,
    margin: 'auto -0.5rem',
    padding: 0,
    '&:first-of-type': {
      borderTop: 'none !important'
    },
    [`@media (max-width: ${theme.navbar.collapseAt})`]: [
      {
        display: 'none',
        flex: '0 1 100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        maxHeight: 'calc(100vh - 60px)',
        padding: '0 5px 80px 5px',
        margin: 'auto 0'
      }
    ]
  })
);

const NavbarInnerContainer = forwardRef<
HTMLOListElement,
NavbarInnerContainerProps
>(({ children, className, variant = 'default', ...rest }, ref) => {
  const { isExpanded } = useContext(NavbarContext);

  return (
    <NavbarInnerContainerList
      ref={ref}
      {...rest}
      className={clsx(className, isExpanded && 'isExpanded')}
      variant={variant}
    >
      {children}
    </NavbarInnerContainerList>
  );
});

NavbarInnerContainer.displayName = 'NavbarInnerContainer';

export { NavbarInnerContainer };
export default NavbarInnerContainer;
